import React, { useState } from 'react';
import { 
  ArrowLeft, 
  User, 
  MapPin, 
  CreditCard, 
  Bell, 
  Shield, 
  HelpCircle, 
  LogOut,
  ChevronRight,
  Moon,
  Globe,
  Smartphone
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { SHOPPER_HOME } from '../../constants/routes';
import { cn } from '../../lib/utils';

interface SettingItemProps {
  icon: React.ElementType;
  label: string;
  description?: string;
  to?: string;
  onClick?: () => void;
  value?: string;
  toggle?: boolean;
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
  toggleDisabled?: boolean;
  danger?: boolean;
}

function SettingItem({ 
  icon: Icon, 
  label, 
  description, 
  to, 
  onClick, 
  value, 
  toggle,
  checked,
  onToggle,
  toggleDisabled,
  danger 
}: SettingItemProps) {
  const content = (
    <div className={cn(
      "flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer",
      danger && "hover:bg-error/5"
    )}>
      <div className="flex items-center space-x-4">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          danger ? "bg-error/10 text-error" : "bg-purple-50 text-primary"
        )}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className={cn("font-medium", danger ? "text-error" : "text-heading")}>{label}</p>
          {description && <p className="text-sm text-body">{description}</p>}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {value && <span className="text-sm text-body">{value}</span>}
        {toggle ? (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!toggleDisabled) onToggle?.(!checked);
            }}
            disabled={toggleDisabled}
            className={cn(
              "w-12 h-7 rounded-full transition-colors relative",
              checked ? "bg-primary" : "bg-gray-200",
              toggleDisabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <div className={cn(
              "absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform",
              checked ? "translate-x-6" : "translate-x-1"
            )} />
          </button>
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </div>
    </div>
  );

  if (to) {
    return <Link to={to}>{content}</Link>;
  }

  return <div onClick={onClick}>{content}</div>;
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const {
    pushPermission,
    pushEnabled,
    requestingPush,
    enablePush,
    disablePush,
    vapidConfigured,
  } = useNotifications();
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('fash-dark-mode') === 'true';
  });

  const handleDarkModeToggle = (enabled: boolean) => {
    setDarkMode(enabled);
    localStorage.setItem('fash-dark-mode', String(enabled));
    document.documentElement.classList.toggle('dark', enabled);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const getPushDescription = () => {
    if (pushPermission === 'unsupported') return 'Not supported in this browser';
    if (pushPermission === 'denied') return 'Blocked — open notification settings';
    if (!vapidConfigured) return 'Setup required in Admin → API Keys';
    if (pushEnabled) return 'Instant alerts enabled';
    return 'Tap to enable browser alerts';
  };

  const pushToggleDisabled =
    requestingPush ||
    pushPermission === 'unsupported' ||
    pushPermission === 'denied' ||
    !vapidConfigured;

  const handlePushToggle = async (enabled: boolean) => {
    if (enabled) {
      await enablePush();
      return;
    }
    await disablePush();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate(SHOPPER_HOME)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" aria-label="Go back">
              <ArrowLeft className="h-6 w-6 text-heading" />
            </button>
            <h1 className="ml-4 text-xl font-serif text-heading">Settings</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Section */}
        <Card>
          <CardContent className="p-0">
            <Link to="/settings/profile" className="flex items-center p-4 hover:bg-gray-50 transition-colors">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-2xl font-serif text-primary">
                {userProfile?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
              <div className="ml-4 flex-1">
                <p className="font-medium text-heading text-lg">
                  {userProfile?.displayName || 'User'}
                </p>
                <p className="text-sm text-body">{user?.email || 'user@example.com'}</p>
                <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-primary text-xs font-medium rounded-full capitalize">
                  {userProfile?.role || 'shopper'}
                </span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>
          </CardContent>
        </Card>

        {/* Account Settings */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Account
          </h2>
          <Card>
            <CardContent className="p-0 divide-y divide-gray-100">
              <SettingItem
                icon={MapPin}
                label="Addresses"
                description="Manage delivery addresses"
                to="/settings/addresses"
              />
              <SettingItem
                icon={CreditCard}
                label="Payment Methods"
                description="Add or remove saved cards"
                to="/settings/payment"
              />
              <SettingItem
                icon={Shield}
                label="Security"
                description="Password and 2FA"
                to="/settings/security"
              />
            </CardContent>
          </Card>
        </div>

        {/* Preferences */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Preferences
          </h2>
          <Card>
            <CardContent className="p-0 divide-y divide-gray-100">
              <SettingItem
                icon={Bell}
                label="Notifications"
                description="Email, SMS, and category preferences"
                to="/settings/notifications"
              />
              <SettingItem
                icon={Smartphone}
                label="Push Notifications"
                description={getPushDescription()}
                toggle
                checked={pushEnabled && pushPermission === 'granted'}
                toggleDisabled={pushToggleDisabled}
                onToggle={handlePushToggle}
              />
              <SettingItem
                icon={Moon}
                label="Dark Mode"
                description={darkMode ? 'On' : 'Off'}
                toggle
                checked={darkMode}
                onToggle={handleDarkModeToggle}
              />
              <SettingItem
                icon={Globe}
                label="Language"
                value="English"
                to="/settings/language"
              />
            </CardContent>
          </Card>
        </div>

        {/* Support */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Support
          </h2>
          <Card>
            <CardContent className="p-0 divide-y divide-gray-100">
              <SettingItem
                icon={HelpCircle}
                label="Help Center"
                to="/help"
              />
              <SettingItem
                icon={Smartphone}
                label="About Fash"
                value="v1.0.0"
                to="/about"
              />
            </CardContent>
          </Card>
        </div>

        {/* Sign Out */}
        <Card>
          <CardContent className="p-0">
            <SettingItem
              icon={LogOut}
              label="Sign Out"
              onClick={handleSignOut}
              danger
            />
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-body py-4">
          <p>Made with ❤️ in NYC</p>
          <p className="mt-1">© 2026 Fash. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
}


